import { useState, useCallback, useRef } from 'react';
import { IUser } from '../types/interfaces/IUser';
import * as userService from '../services/userService';
import { getApiErrorMessage } from '../services/core/apiResponseHelpers';

export function useUsers() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false); // Control para evitar reintentos infinitos
  const retryCountRef = useRef(0);
  const maxRetries = 2; // Máximo 2 reintentos

  const resetErrorState = useCallback(() => {
    setError(null);
    setHasError(false);
    retryCountRef.current = 0;
  }, []);

  const getAllUsers = useCallback(async () => {
    // Si ya hubo un error reciente, no reintentar
    if (hasError && retryCountRef.current >= maxRetries) {
      console.warn('Max retries reached for getAllUsers, skipping API call');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await userService.getUsers();

      if (response.success && response.data) {
        resetErrorState(); // Resetear estado de error en caso de éxito
        return response.data; // Retornar data directa
      } else {
        const errorMsg = getApiErrorMessage(response, 'Error al cargar usuarios');
        setError(errorMsg);
        setHasError(true);
        retryCountRef.current++;
        return null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      setHasError(true);
      retryCountRef.current++;
      return null;
    } finally {
      setLoading(false);
    }
  }, [hasError, resetErrorState]);

  const getUserById = useCallback(
    async (id: string) => {
      // Si ya hubo un error reciente, no reintentar
      if (hasError && retryCountRef.current >= maxRetries) {
        console.warn('Max retries reached for getUserById, skipping API call');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await userService.getUserById(id);

        if (response.success && response.data) {
          resetErrorState(); // Resetear estado de error en caso de éxito
          return response.data; // Retornar data directa
        } else {
          const errorMsg = getApiErrorMessage(response, 'Error al cargar usuario');
          setError(errorMsg);
          setHasError(true);
          retryCountRef.current++;
          return null;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        setHasError(true);
        retryCountRef.current++;
        return null;
      } finally {
        setLoading(false);
      }
    },
    [hasError, resetErrorState]
  );

  const createUser = useCallback(
    async (userData: Omit<IUser, 'id' | 'createdAt' | 'updatedAt'>) => {
      setLoading(true);
      setError(null);

      try {
        const response = await userService.createUser(userData);

        if (response.success && response.data) {
          return response.data; // Retornar data directa
        } else {
          setError(getApiErrorMessage(response, 'Error al crear usuario'));
          return null;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateUser = useCallback(
    async (id: string, userData: Partial<IUser>) => {
      setLoading(true);
      setError(null);

      try {
        const response = await userService.updateUser(id, userData);

        if (response.success && response.data) {
          return response.data; // Retornar data directa
        } else {
          setError(getApiErrorMessage(response, 'Error al actualizar usuario'));
          return null;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteUser = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.deleteUser(id);

      if (response.success) {
        return true; // Retornar éxito
      } else {
        setError(getApiErrorMessage(response, 'Error al eliminar usuario'));
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleUserStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.toggleUserStatus(id);

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        setError(getApiErrorMessage(response, 'Error al cambiar estado del usuario'));
        return null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    getAllUsers, // Retorna IUser[] o null
    getUserById, // Retorna IUser o null
    createUser, // Retorna IUser o null
    updateUser, // Retorna IUser o null
    deleteUser, // Retorna boolean
    toggleUserStatus, // Retorna IUser o null
    loading,
    error,
    hasError,
    resetErrorState,
  };
}
